import * as React from 'react';

import { USER_NOT_PRO_ERROR } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import { AxiosError } from 'axios';

import { UIButton } from '~shared/components/buttons';
import { DialogConfirmation, DialogProps } from '~shared/components/Dialog';

interface ServerResponseData {
  code: string;
  message: string;
}

export interface AxiosServerErrorDialogProps
  extends Pick<DialogProps, 'open' | 'onClose'> {
  error: AxiosError<ServerResponseData>;
}

function ErrorBodyText(errorCode: string) {
  switch (errorCode) {
    case USER_NOT_PRO_ERROR.code:
      return translate(
        `The ability to include images in answers and explanations is only available as part of Socrative Pro. Please remove these images from your question in order to continue.`
      );
    default:
      return translate(
        "Sorry, something unexpected went wrong. Please refresh the page and try again. If problems persist, don’t hesitate to contact our <a href='https://help.socrative.com/'>support team</a>." /* eslint-disable-line */ /* prettier-ignore */
      );
  }
}

export function AxiosServerErrorDialog({
  open,
  onClose,
  error,
}: AxiosServerErrorDialogProps) {
  return (
    <DialogConfirmation
      id="server-error"
      open={open}
      title={translate('Unexpected Error')}
      onClose={onClose}
      closeLabel={translate('Close')}
      actions={[
        <UIButton key="ok" appearance="primary" onClick={onClose}>
          {translate('Ok', true)}
        </UIButton>,
      ]}
    >
      <AxiosErrorMessage error={error} />
    </DialogConfirmation>
  );
}

function AxiosErrorMessage({
  error,
}: {
  error: AxiosError<ServerResponseData>;
}) {
  if (error.response) {
    const {
      response: { status, statusText, data },
    } = error;
    const bodyText = {
      __html: ErrorBodyText(data?.code),
    };

    return (
      <>
        <span
          className="font-sans text-blue-900"
          dangerouslySetInnerHTML={bodyText}
        />
        <p className="font-sans text-blue-600">
          Status: {status} - {statusText}
        </p>
      </>
    );
  } else {
    return (
      <p
        dangerouslySetInnerHTML={{
          __html:
            error?.message ??
            translate(
              "Sorry, something unexpected went wrong. Please refresh the page and try again. If problems persist, don’t hesitate to contact our <a class='link' href='https://help.socrative.com/'>support team</a>."
            ),
        }}
      ></p>
    );
  }
}
